import type {
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IEventAttr } from "./types";

import { EventType } from "gql/graphql";
import { useFiltersStorage } from "storage/filters";
import { castEventType } from "utils/format-helpers";

const EVENT_TYPE_OPTIONS = [...Object.values(EventType)];

const useTodEventsFilters = (
  events: IEventAttr[],
  setFilteredDataset: React.Dispatch<React.SetStateAction<IEventAttr[]>>,
): IUseFilterProps<IEventAttr> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const uniqueStatuses = useMemo(
    (): string[] =>
      Array.from(new Set(events.flatMap((event): string => event.eventStatus))),
    [events],
  );

  const options = useMemo(
    (): IOptionsProps<IEventAttr>[] => [
      {
        filterOptions: [
          { key: "organization", label: "Organization", type: "text" },
        ],
        label: "Organization",
      },
      {
        filterOptions: [
          { key: "groupName", label: "Group name", type: "text" },
        ],
        label: "Group name",
      },
      {
        filterOptions: [
          { key: "eventDate", label: "Event date", type: "dateRange" },
        ],
        label: "Event date",
      },
      {
        filterOptions: [
          {
            filterFn: "includesInsensitive",
            key: "detail",
            label: "Description",
            type: "text",
          },
        ],
        label: "Description",
      },
      {
        filterOptions: [
          {
            key: "eventType",
            label: "Type",
            options: EVENT_TYPE_OPTIONS.map(
              (type): IOptions => ({
                label: t(castEventType(type)),
                value: t(castEventType(type)),
              }),
            ),
            type: "checkboxes",
          },
        ],
        label: "Type",
      },
      {
        filterOptions: [
          {
            key: "eventStatus",
            label: "Status",
            options: uniqueStatuses.map(
              (status): IOptions => ({ label: status, value: status }),
            ),
            type: "select",
          },
        ],
        label: "Status",
      },
    ],
    [uniqueStatuses, t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("todo_events") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("todo_events", value);
    },
    [setValues],
  );

  return useFilters({
    dataset: events,
    getValues: handleGetValues,
    options,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useTodEventsFilters };
